package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeColors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Position for the tooltip relative to its anchor
 */
enum class TooltipPosition {
    TOP,
    BOTTOM,
    LEFT,
    RIGHT
}

/**
 * GuidedTooltip - Educational tooltip with pointer for onboarding.
 *
 * Used to highlight and explain UI elements during first-time user experience.
 * Features:
 * - Animated entrance
 * - Pointer arrow indicating target element
 * - Dismiss button
 * - Step indicator for multi-step guides
 *
 * @param title Bold title text
 * @param description Explanation text
 * @param position Where to show relative to anchor (TOP, BOTTOM, LEFT, RIGHT)
 * @param currentStep Current step number (1-indexed, for display)
 * @param totalSteps Total steps in the guide
 * @param onDismiss Callback when user dismisses the tooltip
 * @param onNext Optional callback for "Next" button (if null, shows "Got it" instead)
 */
@Composable
fun GuidedTooltip(
    title: String,
    description: String,
    position: TooltipPosition = TooltipPosition.BOTTOM,
    currentStep: Int? = null,
    totalSteps: Int? = null,
    accentColor: CSSColorValue? = null,
    onDismiss: () -> Unit,
    onNext: (() -> Unit)? = null
) {
    val colors = Theme.colors

    // Container with pointer
    Div({
        style {
            position(Position.Relative)
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            property("box-shadow", "0 8px 24px rgba(0, 0, 0, 0.2)")
            maxWidth(280.px)
            property("z-index", "1000")
            property("animation", "tooltipFadeIn 0.3s ease-out")

            // Accent border
            accentColor?.let {
                property("border-left", "4px solid $it")
            }
        }
    }) {
        // Pointer arrow based on position
        TooltipPointer(position, colors, accentColor)

        // Step indicator (if multi-step)
        if (currentStep != null && totalSteps != null) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(8.px)
                }
            }) {
                // Step badge
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(colors.textSecondary)
                        backgroundColor(colors.surfaceVariant)
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                        property("text-transform", "uppercase")
                        letterSpacing(0.5.px)
                    }
                }) {
                    Text("Step $currentStep of $totalSteps")
                }

                // Skip link
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                        cursor("pointer")
                    }
                    onClick { onDismiss() }
                }) {
                    Text("Skip all")
                }
            }
        }

        // Title
        Div({
            style {
                fontSize(15.px)
                fontWeight(700)
                color(colors.textPrimary)
                marginBottom(6.px)
            }
        }) {
            Text(title)
        }

        // Description
        P({
            style {
                fontSize(13.px)
                color(colors.textSecondary)
                lineHeight("1.5")
                margin(0.px, 0.px, 12.px, 0.px)
            }
        }) {
            Text(description)
        }

        // Action button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
            }
        }) {
            Button(
                text = if (onNext != null) "Next" else "Got it",
                onClick = { onNext?.invoke() ?: onDismiss() },
                variant = "primary"
            )
        }
    }
}

@Composable
private fun TooltipPointer(
    position: TooltipPosition,
    colors: ThemeColors,
    @Suppress("UNUSED_PARAMETER") accentColor: CSSColorValue? // Reserved for future accent pointer styling
) {
    val pointerColor = colors.surface.toString()

    Div({
        style {
            position(Position.Absolute)
            width(0.px)
            height(0.px)

            when (position) {
                TooltipPosition.TOP -> {
                    // Pointer points down (tooltip is above element)
                    bottom((-8).px)
                    left(50.percent)
                    property("transform", "translateX(-50%)")
                    property("border-left", "8px solid transparent")
                    property("border-right", "8px solid transparent")
                    property("border-top", "8px solid $pointerColor")
                }
                TooltipPosition.BOTTOM -> {
                    // Pointer points up (tooltip is below element)
                    top((-8).px)
                    left(50.percent)
                    property("transform", "translateX(-50%)")
                    property("border-left", "8px solid transparent")
                    property("border-right", "8px solid transparent")
                    property("border-bottom", "8px solid $pointerColor")
                }
                TooltipPosition.LEFT -> {
                    // Pointer points right (tooltip is to the left of element)
                    right((-8).px)
                    top(50.percent)
                    property("transform", "translateY(-50%)")
                    property("border-top", "8px solid transparent")
                    property("border-bottom", "8px solid transparent")
                    property("border-left", "8px solid $pointerColor")
                }
                TooltipPosition.RIGHT -> {
                    // Pointer points left (tooltip is to the right of element)
                    left((-8).px)
                    top(50.percent)
                    property("transform", "translateY(-50%)")
                    property("border-top", "8px solid transparent")
                    property("border-bottom", "8px solid transparent")
                    property("border-right", "8px solid $pointerColor")
                }
            }
        }
    })
}

/**
 * Wrapper component that positions a GuidedTooltip relative to content.
 * Use this to wrap UI elements that need onboarding tooltips.
 */
@Composable
fun GuidedTooltipWrapper(
    showTooltip: Boolean,
    title: String,
    description: String,
    position: TooltipPosition = TooltipPosition.BOTTOM,
    currentStep: Int? = null,
    totalSteps: Int? = null,
    accentColor: CSSColorValue? = null,
    onDismiss: () -> Unit,
    onNext: (() -> Unit)? = null,
    content: @Composable () -> Unit
) {
    Div({
        style {
            position(Position.Relative)
            display(DisplayStyle.InlineBlock)
        }
    }) {
        // Main content
        content()

        // Tooltip overlay
        if (showTooltip) {
            Div({
                style {
                    position(Position.Absolute)
                    property("z-index", "1000")

                    when (position) {
                        TooltipPosition.TOP -> {
                            bottom(100.percent)
                            left(50.percent)
                            property("transform", "translateX(-50%)")
                            marginBottom(12.px)
                        }
                        TooltipPosition.BOTTOM -> {
                            top(100.percent)
                            left(50.percent)
                            property("transform", "translateX(-50%)")
                            marginTop(12.px)
                        }
                        TooltipPosition.LEFT -> {
                            right(100.percent)
                            top(50.percent)
                            property("transform", "translateY(-50%)")
                            marginRight(12.px)
                        }
                        TooltipPosition.RIGHT -> {
                            left(100.percent)
                            top(50.percent)
                            property("transform", "translateY(-50%)")
                            marginLeft(12.px)
                        }
                    }
                }
            }) {
                GuidedTooltip(
                    title = title,
                    description = description,
                    position = position,
                    currentStep = currentStep,
                    totalSteps = totalSteps,
                    accentColor = accentColor,
                    onDismiss = onDismiss,
                    onNext = onNext
                )
            }
        }
    }
}

/**
 * Data class for defining a guided tour step
 */
data class GuidedTourStep(
    val id: String,
    val title: String,
    val description: String,
    val position: TooltipPosition = TooltipPosition.BOTTOM,
    val accentColor: CSSColorValue? = null
)

/**
 * State holder for a guided tour
 */
class GuidedTourState(
    private val steps: List<GuidedTourStep>,
    private val onComplete: () -> Unit
) {
    var currentStepIndex by mutableStateOf(0)
        private set

    val currentStep: GuidedTourStep?
        get() = steps.getOrNull(currentStepIndex)

    val totalSteps: Int
        get() = steps.size

    val isComplete: Boolean
        get() = currentStepIndex >= steps.size

    fun isStepActive(stepId: String): Boolean {
        return currentStep?.id == stepId
    }

    fun nextStep() {
        if (currentStepIndex < steps.size - 1) {
            currentStepIndex++
        } else {
            currentStepIndex = steps.size
            onComplete()
        }
    }

    fun skipAll() {
        currentStepIndex = steps.size
        onComplete()
    }
}

/**
 * Remember a guided tour state
 */
@Composable
fun rememberGuidedTourState(
    steps: List<GuidedTourStep>,
    onComplete: () -> Unit
): GuidedTourState {
    return remember { GuidedTourState(steps, onComplete) }
}
